/**
 * Mock Data for Development/Testing
 *
 * Simulates Shopify product data for UI testing
 */

import type { Product, Shop, OptimizationJob, DashboardStats, BillingPlan } from '../types';

export const mockShop: Shop = {
  id: 'demo-shop.myshopify.com',
  shopName: 'Demo Outdoor Shop',
  email: 'info@demo-outdoor.de',
  plan: 'starter',
  productsOptimized: 23,
  productsLimit: 100,
};

export const mockProducts: Product[] = [
  {
    id: 'prod-001',
    shopifyId: 'gid://shopify/Product/7654321001',
    title: 'Ultraleicht Wanderrucksack 45L',
    descriptionOriginal: `<p>Leichter Rucksack für mehrtägige Wanderungen. 45 Liter Volumen. Wasserdichtes Material. Ergonomisches Tragesystem.</p>`,
    descriptionOptimized: `<h2>Der perfekte Begleiter für ambitionierte Wanderer</h2>
<p>Der Ultraleicht Wanderrucksack 45L vereint minimales Gewicht mit maximalem Komfort. Mit nur 980g ist er einer der leichtesten Rucksäcke seiner Klasse – ohne Kompromisse bei der Funktionalität.</p>
<h3>Durchdachte Features für lange Touren</h3>
<p>Das ergonomische Tragesystem verteilt das Gewicht optimal auf Hüfte und Schultern. Die atmungsaktive Rückenpolsterung sorgt auch bei sommerlichen Temperaturen für angenehmen Tragekomfort.</p>
<p>Das wasserdichte Ripstop-Nylon hält selbst bei Starkregen dicht. Alle Nähte sind versiegelt, der wasserdichte Rollverschluss schützt Ihre Ausrüstung zuverlässig.</p>`,
    usecases: [
      'Ideal für: Mehrtägige Hüttentouren in den Alpen',
      'Perfekt für: Ultralight-Trekking und Fernwanderwege',
      'Optimal für: Backpacker die jedes Gramm zählen',
    ],
    faq: [
      { question: 'Wie viel wiegt der Rucksack?', answer: 'Der Rucksack wiegt nur 980g und gehört damit zu den leichtesten 45L-Rucksäcken am Markt.' },
      { question: 'Ist der Rucksack wasserdicht?', answer: 'Ja, das Ripstop-Nylon ist wasserdicht beschichtet und alle Nähte sind versiegelt. Der Rollverschluss bietet zusätzlichen Schutz.' },
      { question: 'Für welche Körpergröße ist er geeignet?', answer: 'Das verstellbare Tragesystem passt sich Körpergrößen von 160-195cm optimal an.' },
    ],
    specs: [
      { key: 'Volumen', value: '45 Liter' },
      { key: 'Gewicht', value: '980g' },
      { key: 'Material', value: 'Ripstop-Nylon 40D' },
      { key: 'Wassersäule', value: '10.000mm' },
    ],
    status: 'optimized',
    optimizedAt: '2024-01-07T14:30:00Z',
  },
  {
    id: 'prod-002',
    shopifyId: 'gid://shopify/Product/7654321002',
    title: 'Merino Funktionsshirt Herren',
    descriptionOriginal: `<p>Funktionsshirt aus 100% Merinowolle. Temperaturregulierend. Geruchshemmend. Für Sport und Alltag.</p>`,
    descriptionOptimized: `<h2>Natürlicher Komfort für aktive Männer</h2>
<p>Unser Merino Funktionsshirt kombiniert die besten Eigenschaften der Natur mit modernem Design. Die feine Merinowolle (18.5 Mikron) fühlt sich seidenweich auf der Haut an – ohne das typische Kratzen.</p>
<h3>Temperaturregulierung wie von der Natur vorgesehen</h3>
<p>Merinowolle reguliert aktiv Ihre Körpertemperatur: kühlend bei Anstrengung, wärmend in Ruhephasen. Die natürliche Geruchshemmung ermöglicht mehrtägiges Tragen ohne Waschen – perfekt für Reisen und Outdoor-Abenteuer.</p>`,
    usecases: [
      'Ideal für: Trekking und Bergtouren',
      'Perfekt für: Reisen mit leichtem Gepäck',
      'Optimal für: Sport und aktiven Alltag',
    ],
    faq: [
      { question: 'Kratzt das Shirt?', answer: 'Nein, wir verwenden superfeine Merinowolle mit 18.5 Mikron. Das ist feiner als die meisten Baumwoll-Shirts und fühlt sich seidenweich an.' },
      { question: 'Wie pflege ich das Shirt?', answer: 'Maschinenwäsche bei 30°C im Wollprogramm. Nicht in den Trockner. Oft reicht auch Auslüften.' },
    ],
    specs: [
      { key: 'Material', value: '100% Merinowolle' },
      { key: 'Grammatur', value: '150g/m²' },
      { key: 'Faserfeinheit', value: '18.5 Mikron' },
    ],
    status: 'applied',
    optimizedAt: '2024-01-05T10:15:00Z',
    appliedAt: '2024-01-05T11:00:00Z',
  },
  {
    id: 'prod-003',
    shopifyId: 'gid://shopify/Product/7654321003',
    title: 'LED Stirnlampe 1000 Lumen',
    descriptionOriginal: `<p>Helle Stirnlampe mit 1000 Lumen. USB aufladbar. 5 Leuchtmodi. IPX6 wasserdicht. Ideal für Outdoor und Handwerk.</p>`,
    status: 'pending',
  },
  {
    id: 'prod-004',
    shopifyId: 'gid://shopify/Product/7654321004',
    title: 'Camping Hängematte mit Moskitonetz',
    descriptionOriginal: `<p>Ultraleichte Hängematte mit integriertem Moskitonetz. Schneller Aufbau. Trägt bis 200kg. Inkl. Aufhängeset.</p>`,
    status: 'pending',
  },
  {
    id: 'prod-005',
    shopifyId: 'gid://shopify/Product/7654321005',
    title: 'Trekking Sandalen Unisex',
    descriptionOriginal: `<p>Robuste Sandalen für Wanderungen und Wassersport. Schnelltrocknendes Material. Rutschfeste Vibram-Sohle.</p>`,
    status: 'pending',
  },
  {
    id: 'prod-006',
    shopifyId: 'gid://shopify/Product/7654321006',
    title: 'Kompakt Fernglas 10x42',
    descriptionOriginal: `<p>Hochwertiges Fernglas für Naturbeobachtung. 10-fache Vergrößerung. Stickstoffgefüllt. Wasserdicht.</p>`,
    descriptionOptimized: `<h2>Kristallklare Sicht in jeder Situation</h2>
<p>Das Kompakt Fernglas 10x42 bietet Profi-Optik im handlichen Format. Die vollvergüteten BaK-4 Prismen liefern gestochen scharfe Bilder mit natürlicher Farbwiedergabe.</p>`,
    usecases: [
      'Ideal für: Vogelbeobachtung und Wildlife',
      'Perfekt für: Wanderungen und Naturerlebnisse',
    ],
    faq: [
      { question: 'Ist das Fernglas für Brillenträger geeignet?', answer: 'Ja, die umklappbaren Augenmuscheln ermöglichen einen optimalen Augenabstand auch mit Brille.' },
    ],
    specs: [
      { key: 'Vergrößerung', value: '10x' },
      { key: 'Objektivdurchmesser', value: '42mm' },
      { key: 'Sehfeld', value: '105m auf 1000m' },
    ],
    status: 'optimized',
    optimizedAt: '2024-01-06T16:45:00Z',
  },
  {
    id: 'prod-007',
    shopifyId: 'gid://shopify/Product/7654321007',
    title: 'Isolierte Trinkflasche 750ml',
    descriptionOriginal: `<p>Doppelwandige Edelstahl-Trinkflasche. Hält 24h kalt, 12h warm. BPA-frei. Auslaufsicher.</p>`,
    status: 'pending',
  },
  {
    id: 'prod-008',
    shopifyId: 'gid://shopify/Product/7654321008',
    title: 'Multitool 18-in-1',
    descriptionOriginal: `<p>Kompaktes Multitool aus Edelstahl. 18 Funktionen inkl. Zange, Messer, Säge. Mit Gürteltasche.</p>`,
    status: 'pending',
  },
];

export const mockDashboardStats: DashboardStats = {
  totalProducts: mockProducts.length,
  optimizedProducts: mockProducts.filter(p => p.status === 'optimized' || p.status === 'applied').length,
  appliedProducts: mockProducts.filter(p => p.status === 'applied').length,
  pendingProducts: mockProducts.filter(p => p.status === 'pending').length,
  usagePercent: 23,
  plan: 'Starter',
  productsLimit: 100,
};

/**
 * Billing Plans - Updated 2026-01-11
 *
 * Pricing validated for:
 * - 80%+ net margin (after Shopify 20% cut)
 * - Competitive positioning vs Booster SEO, Avada SEO
 * - Fast market penetration strategy
 * - Price-per-product degression: 37-59% per tier
 */
export const mockBillingPlans: BillingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    productsLimit: 25,
    features: [
      'bis 25 Produkte/Monat',
      'KI-Beschreibungsoptimierung',
      'Use-Cases & FAQ',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 39,
    productsLimit: 100,
    features: [
      'bis 100 Produkte/Monat',
      'KI-Beschreibungsoptimierung',
      'Use-Cases & FAQ',
      'Technische Specs',
      'Email-Support',
    ],
    recommended: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    productsLimit: 500,
    features: [
      'bis 500 Produkte/Monat',
      'Alle Starter-Features',
      'Bulk-Verarbeitung',
      'Priority Support',
      'API-Zugang',
    ],
  },
  {
    id: 'business',
    name: 'Business',
    price: 199,
    productsLimit: 2000,
    features: [
      'bis 2.000 Produkte/Monat',
      'Alle Pro-Features',
      'Erweiterte Analytics',
      'Account Manager',
      'SLA 99,9%',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: -1, // Custom pricing (Projektgeschäft)
    productsLimit: -1, // Unlimited
    features: [
      'Unbegrenzte Produkte',
      'Alle Business-Features',
      'Custom AI-Training',
      'White-Label Option',
      'Dedicated Infrastructure',
      'Projektspezifische Preisgestaltung',
    ],
  },
];

// Simulate an optimization job
let mockCurrentJob: OptimizationJob | null = null;

export function createMockJob(productIds: string[]): OptimizationJob {
  mockCurrentJob = {
    id: `job-${Date.now()}`,
    status: 'processing',
    totalProducts: productIds.length,
    processedProducts: 0,
    successCount: 0,
    errorCount: 0,
    createdAt: new Date().toISOString(),
  };

  // Simulate progress
  simulateJobProgress(productIds);

  return mockCurrentJob;
}

function simulateJobProgress(productIds: string[]) {
  let processed = 0;
  const interval = setInterval(() => {
    if (!mockCurrentJob) {
      clearInterval(interval);
      return;
    }

    processed++;
    mockCurrentJob.processedProducts = processed;
    mockCurrentJob.successCount = processed;

    if (processed >= productIds.length) {
      mockCurrentJob.status = 'completed';
      mockCurrentJob.completedAt = new Date().toISOString();
      clearInterval(interval);
    }
  }, 1500); // 1.5 seconds per product
}

export function getMockJob(jobId: string): OptimizationJob | null {
  if (mockCurrentJob?.id === jobId) {
    return mockCurrentJob;
  }
  return null;
}
